import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Trash2, BadgeDollarSign, Loader2 } from "lucide-react";

export default function RoyaltyRosterPanel() {
  const [roster, setRoster] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [pcts, setPcts] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/royalty-roster").then(({ data }) => setRoster(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const search = async (v) => {
    setQ(v);
    if (v.length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/members/search?q=${encodeURIComponent(v)}`);
      setResults(data.filter((p) => !roster.some((r) => r.player_id === p.id)).slice(0, 6));
    } catch { setResults([]); }
  };

  const add = async (p) => {
    setBusy(true);
    try {
      await api.post("/admin/royalty-roster", { player_id: p.id, share_pct: 50 });
      setQ(""); setResults([]);
      load();
      toast.success(`${p.name} added to the royalty roster at 50%`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const savePct = async (r) => {
    const pct = Number(pcts[r.player_id] ?? r.share_pct);
    try {
      await api.post("/admin/royalty-roster", { player_id: r.player_id, share_pct: pct });
      load();
      toast.success(`${r.name} — royalty share set to ${pct}%`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (r) => {
    try { await api.delete(`/admin/royalty-roster/${r.player_id}`); load(); toast.success(`${r.name} removed from the roster`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="royalty-roster-panel">
      <p className="text-xs text-zinc-500">
        Players on this roster get paid their custom royalty share every time one of their specialty cards is sold on the
        Marketplace or pulled from a pack. When you mint a card in the Card Forge below and feature a roster player, their
        share is applied automatically.
      </p>
      <div className="relative max-w-sm">
        <div className="flex items-center gap-2 bg-pb-elevated border border-pb-border px-3">
          <Search className="w-3.5 h-3.5 text-zinc-500" />
          <input data-testid="roster-search-input" value={q} onChange={(e) => search(e.target.value)}
            placeholder="Search a player to add…"
            className="flex-1 bg-transparent py-2.5 text-sm text-white focus:outline-none" />
          {q && <button data-testid="roster-search-clear" onClick={() => { setQ(""); setResults([]); }}><X className="w-3.5 h-3.5 text-zinc-500" /></button>}
        </div>
        {results.length > 0 && (
          <div className="absolute z-10 w-full bg-pb-surface border border-pb-border max-h-44 overflow-y-auto">
            {results.map((p) => (
              <button key={p.id} data-testid={`roster-add-${p.id}`} onClick={() => add(p)} disabled={busy}
                className="w-full text-left px-3 py-2 text-sm hover:bg-pb-elevated flex items-center justify-between">
                <span>{p.name}</span>
                <span className="text-[9px] uppercase tracking-widest text-pb-red font-black">+ Add</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="divide-y divide-pb-border/40 border border-pb-border" data-testid="roster-list">
        {roster.length === 0 && <div className="p-4 text-xs text-zinc-600">No players on the roster yet — search above to add one.</div>}
        {roster.map((r) => (
          <div key={r.player_id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`roster-row-${r.player_id}`}>
            <div className="flex-1 min-w-[140px]">
              <div className="text-sm font-semibold">{r.name}</div>
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">{r.cards} specialty card{r.cards === 1 ? "" : "s"}</div>
            </div>
            <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-mono" data-testid={`roster-earned-${r.player_id}`}>
              <BadgeDollarSign className="w-3.5 h-3.5" /> {r.earned} BLC earned
            </div>
            <div className="flex items-center gap-1">
              <input data-testid={`roster-pct-${r.player_id}`} type="number" min={0} max={100} step={5}
                value={pcts[r.player_id] ?? r.share_pct}
                onChange={(e) => setPcts({ ...pcts, [r.player_id]: e.target.value })}
                className="w-16 bg-pb-elevated border border-pb-border px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-pb-red" />
              <span className="text-[10px] text-zinc-500">%</span>
            </div>
            <button data-testid={`roster-save-${r.player_id}`} onClick={() => savePct(r)}
              className="text-[9px] font-black uppercase tracking-widest bg-pb-elevated border border-pb-border px-3 py-2 hover:border-pb-red hover:text-pb-red transition-colors">
              Save
            </button>
            <button data-testid={`roster-remove-${r.player_id}`} onClick={() => remove(r)} title="Remove from roster"
              className="p-2 text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
